import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

const HtmlEmailDisplay = ({ htmlContent }) => {
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(500);

    // Configure DOMPurify for email-safe HTML
    const getSanitizedHtml = () => {
        DOMPurify.addHook('afterSanitizeAttributes', function(node) {
            if (node.tagName === 'A' && node.getAttribute('href')) {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            }
        });

        return DOMPurify.sanitize(htmlContent, {
            ALLOWED_TAGS: [
                'a', 'b', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'i', 'img', 'li', 'ol', 'p', 'span', 'strong', 'table', 'tbody',
                'td', 'th', 'thead', 'tr', 'u', 'ul', 'style', 'font', 'center',
                'hr', 'blockquote', 'pre', 'code', 'col', 'colgroup', 'strike',
                'sup', 'sub', 'section', 'article', 'header', 'footer', 'nav',
                'aside', 'main', 'figure', 'figcaption', 'caption', 'small',
                'address', 'time', 'abbr', 'cite', 'q', 'dfn', 'mark', 'ins', 'del',
                'head', 'title', 'meta', 'link', 'body', 'html'
            ],
            ALLOWED_ATTR: [
                'align', 'alt', 'bgcolor', 'border', 'cellpadding', 'cellspacing',
                'class', 'color', 'colspan', 'dir', 'face', 'height', 'href', 'id',
                'lang', 'rowspan', 'size', 'src', 'style', 'target', 'title',
                'rel', 'valign', 'width', 'background', 'marginwidth', 'marginheight',
                'name', 'content', 'http-equiv', 'charset', 'type', 'media'
            ],
            ALLOW_DATA_ATTR: false,
            WHOLE_DOCUMENT: true
        });
    };

    // Create a complete HTML document for the iframe
    const getIframeContent = () => {
        const sanitizedHtml = getSanitizedHtml();

        // Check if it's already a complete HTML document
        const isCompleteDocument = /<html[\s>]/i.test(sanitizedHtml) || /<body[\s>]/i.test(sanitizedHtml);

        if (isCompleteDocument) {
            // Inject our base styles into existing document
            return sanitizedHtml.replace(
                /<head[^>]*>/i,
                `<head><base target="_blank"><style>
                    body {
                        margin: 0 !important;
                        padding: 16px !important;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                    }
                    img { max-width: 100% !important; height: auto !important; }
                    a { color: #1a73e8; }
                    table { max-width: 100% !important; }
                </style>`
            );
        }

        // Wrap in a complete HTML document
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <base target="_blank">
                <style>
                    * {
                        box-sizing: border-box;
                    }
                    body {
                        margin: 0;
                        padding: 20px;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                        font-size: 14px;
                        line-height: 1.6;
                        color: #1f2937;
                        background: #ffffff;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                    }
                    img {
                        max-width: 100%;
                        height: auto;
                        display: block;
                    }
                    table {
                        max-width: 100%;
                        border-collapse: collapse;
                    }
                    a {
                        color: #1a73e8;
                        text-decoration: none;
                    }
                    a:hover {
                        text-decoration: underline;
                    }
                    pre, code {
                        background: #f3f4f6;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                        font-size: 13px;
                    }
                    pre {
                        padding: 12px;
                        overflow-x: auto;
                    }
                    blockquote {
                        margin: 16px 0;
                        padding: 12px 20px;
                        border-left: 4px solid #e5e7eb;
                        background: #f9fafb;
                        color: #4b5563;
                    }
                    hr {
                        border: none;
                        border-top: 1px solid #e5e7eb;
                        margin: 20px 0;
                    }
                    h1, h2, h3, h4, h5, h6 {
                        margin-top: 24px;
                        margin-bottom: 12px;
                        font-weight: 600;
                        line-height: 1.3;
                        color: #111827;
                    }
                    p {
                        margin: 0 0 16px 0;
                    }
                    ul, ol {
                        margin: 0 0 16px 0;
                        padding-left: 24px;
                    }
                    li {
                        margin-bottom: 8px;
                    }
                </style>
            </head>
            <body>
                ${sanitizedHtml}
            </body>
            </html>
        `;
    };

    // Adjust iframe height based on content
    useEffect(() => {
        const adjustHeight = () => {
            if (iframeRef.current) {
                try {
                    const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
                    if (iframeDoc && iframeDoc.body) {
                        const newHeight = Math.max(
                            iframeDoc.body.scrollHeight,
                            iframeDoc.documentElement.scrollHeight,
                            300
                        );
                        setIframeHeight(Math.min(newHeight + 40, 800)); // Cap at 800px
                    }
                } catch (e) {
                    console.log('Could not access iframe content for height adjustment');
                }
            }
        };

        // Adjust on load and after images load
        const iframe = iframeRef.current;
        if (iframe) {
            iframe.addEventListener('load', () => {
                adjustHeight();
                // Adjust again after a delay for images
                setTimeout(adjustHeight, 500);
                setTimeout(adjustHeight, 1500);
            });
        }

        return () => {
            if (iframe) {
                iframe.removeEventListener('load', adjustHeight);
            }
        };
    }, [htmlContent]);

    return (
        <div style={{
            position: 'relative',
            background: '#ffffff'
        }}>
            <iframe
                ref={iframeRef}
                srcDoc={getIframeContent()}
                title="Email Content"
                style={{
                    width: '100%',
                    height: `${iframeHeight}px`,
                    border: 'none',
                    display: 'block',
                    background: '#ffffff'
                }}
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
        </div>
    );
};

HtmlEmailDisplay.propTypes = {
    htmlContent: PropTypes.string.isRequired
};

export default HtmlEmailDisplay;
